<template>
  <div class="isc-card">

    <!-- ── Before start / loading ── -->
    <template v-if="!sessionStarted && !sessionCompleted">
      <div class="isc-start">

        <!-- autoStart=true (onboarding): skip intro, show only loading spinner -->
        <template v-if="autoStart">
          <div class="isc-loading">
            <div class="isc-spinner"></div>
            <p class="isc-loading-title">Preparing your skills check…</p>
            <p class="isc-loading-sub">Selecting questions based on the curriculum</p>
          </div>
        </template>

        <!-- Normal start screen with intro copy and button -->
        <template v-else>
          <span class="isc-start-icon">🎯</span>
          <p class="isc-eyebrow">Skills Check</p>
          <h2 class="isc-h2">Let's find the perfect starting point</h2>
          <p class="isc-sub">
            Answer questions across different topics so we can build
            <strong>{{ childName }}</strong>'s personalised learning plan.
          </p>

          <template v-if="!loading">
            <p v-if="startError" class="isc-error">{{ startError }}</p>
            <button type="button" class="isc-btn-gold isc-btn-full" @click="startSession">
              Start Skills Check
            </button>
          </template>

          <template v-else>
            <div class="isc-loading">
              <div class="isc-spinner"></div>
              <p class="isc-loading-title">Preparing your skills check…</p>
              <p class="isc-loading-sub">Selecting questions based on the curriculum</p>
            </div>
          </template>

          <div class="isc-back-row">
            <button type="button" class="isc-btn-back" @click="$emit('back')">← Back</button>
          </div>
        </template>

      </div>
    </template>

    <!-- ── In progress ── -->
    <template v-else-if="sessionStarted && !sessionCompleted">
      <div class="isc-progress-row">
        <span class="isc-progress-count">{{ currentIndex + 1 }} / {{ totalQuestions }} answered</span>
        <span v-if="currentTopicLabel" class="isc-topic-pill">{{ currentTopicLabel }}</span>
      </div>

      <ConfidenceMeter :confidence="confidence" class="isc-meter" />

      <QuestionBlock
        v-if="currentQuestion"
        :question="currentQuestion.question"
        @questionCompleted="submitQuestionResult"
      />

      <div class="isc-stop-row">
        <button type="button" class="isc-btn-stop" @click="handleStop">
          Stop &amp; save progress
        </button>
      </div>
    </template>

    <!-- ── Completed (natural) ── -->
    <template v-else-if="sessionCompleted && !wasStopped">
      <div class="isc-complete">
        <div class="isc-complete-icon">🎉</div>
        <p class="isc-eyebrow">All done</p>
        <h2 class="isc-h2">Skills Check Complete!</h2>
        <p class="isc-sub">
          We've built a personalised learning plan for <strong>{{ childName }}</strong> based on the results.
        </p>
        <button type="button" class="isc-btn-gold isc-btn-full" @click="$emit('complete')">
          Go to {{ childName }}'s Dashboard →
        </button>
      </div>
    </template>

    <!-- ── Stopped early ── -->
    <template v-else>
      <div class="isc-complete">
        <div class="isc-stopped-icon">📋</div>
        <p class="isc-eyebrow">Progress saved</p>
        <h2 class="isc-h2">Skills Check Saved</h2>
        <p class="isc-sub">
          We've saved <strong>{{ childName }}</strong>'s progress from the {{ currentIndex }} question{{ currentIndex === 1 ? '' : 's' }} answered.
          You can complete the full skills check any time from the dashboard.
        </p>
        <button type="button" class="isc-btn-gold isc-btn-full" @click="$emit('stopped')">
          Go to Parent Dashboard →
        </button>
      </div>
    </template>

  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useDiagnosticSession } from '@/composables/useDiagnosticSession';
import ConfidenceMeter from '@/components/ui/ConfidenceMeter.vue';
import QuestionBlock from '@/components/questions/QuestionBlock.vue';

const props = defineProps({
  childId:   { type: String,  required: true },
  childName: { type: String,  default: 'your child' },
  autoStart: { type: Boolean, default: false },
});

defineEmits(['complete', 'stopped', 'back']);

const {
  sessionStarted,
  sessionCompleted,
  currentQuestion,
  currentIndex,
  totalQuestions,
  confidence,
  currentTopicLabel,
  start,
  submitQuestionResult,
  stopSession,
} = useDiagnosticSession(props.childId);

const loading = ref(false);
const startError = ref('');
const wasStopped = ref(false);

onMounted(() => {
  if (props.autoStart) startSession();
});

async function startSession() {
  loading.value = true;
  startError.value = '';
  await start();
  loading.value = false;
  if (!sessionStarted.value) {
    startError.value = 'No questions available. Please try again later.';
  }
}

function handleStop() {
  wasStopped.value = true;
  stopSession();
}
</script>

<style scoped>
/* ── Card ── */
.isc-card {
  background: #F9F6EF;
  border-radius: 8px;
  padding: 40px;
  box-shadow: 0 40px 80px rgba(0,0,0,0.4), 0 0 0 1px rgba(184,144,42,0.15);
  font-family: 'Jost', sans-serif;
}

/* ── Start screen ── */
.isc-start { text-align: center; }

.isc-start-icon {
  font-size: 44px;
  display: block;
  margin-bottom: 14px;
}

.isc-eyebrow {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #B8902A;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}
.isc-eyebrow::before,
.isc-eyebrow::after {
  content: '';
  width: 22px;
  height: 1px;
  background: #B8902A;
  flex-shrink: 0;
}

.isc-h2 {
  font-family: 'Cormorant Garamond', serif;
  font-size: 30px;
  font-weight: 600;
  color: #0D1628;
  line-height: 1.2;
  margin-bottom: 10px;
  letter-spacing: -0.01em;
}

.isc-sub {
  font-size: 14px;
  color: #777;
  font-weight: 300;
  line-height: 1.65;
  margin-bottom: 28px;
  max-width: 420px;
  margin-left: auto;
  margin-right: auto;
}
.isc-sub strong { font-weight: 600; color: #0D1628; }

.isc-error {
  font-size: 13px;
  color: #b91c1c;
  margin-bottom: 12px;
  background: #fdf2f2;
  border: 1px solid #f5c6c6;
  border-radius: 4px;
  padding: 9px 14px;
}

/* ── Buttons ── */
.isc-btn-gold {
  background: #B8902A;
  color: #0D1628;
  border: none;
  border-radius: 4px;
  padding: 12px 28px;
  font-size: 12px;
  font-weight: 700;
  font-family: 'Jost', sans-serif;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}
.isc-btn-gold:hover {
  background: #d4a843;
  transform: translateY(-1px);
}
.isc-btn-full {
  width: 100%;
  padding: 14px;
  font-size: 13px;
}

.isc-btn-back {
  background: transparent;
  border: none;
  color: #B8902A;
  font-size: 13px;
  font-weight: 600;
  font-family: 'Jost', sans-serif;
  cursor: pointer;
  transition: color 0.2s;
  padding: 0;
}
.isc-btn-back:hover { color: #d4a843; }

/* ── Loading ── */
.isc-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 12px 0 20px;
}
.isc-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid rgba(184,144,42,0.2);
  border-top-color: #B8902A;
  border-radius: 50%;
  animation: isc-spin 0.8s linear infinite;
}
@keyframes isc-spin { to { transform: rotate(360deg); } }
.isc-loading-title {
  font-size: 14px;
  font-weight: 600;
  color: #0D1628;
  margin: 0;
}
.isc-loading-sub {
  font-size: 12px;
  color: #aaa;
  font-weight: 300;
  margin: 0;
}

.isc-back-row {
  display: flex;
  align-items: center;
  padding-top: 20px;
  margin-top: 20px;
  border-top: 1px solid #e8e4da;
}

/* ── In-progress ── */
.isc-progress-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.isc-progress-count {
  font-size: 13px;
  font-weight: 600;
  color: #555;
}
.isc-topic-pill {
  font-size: 11px;
  font-weight: 700;
  background: rgba(184,144,42,0.1);
  color: #7a5c10;
  padding: 4px 12px;
  border-radius: 100px;
  letter-spacing: 0.04em;
  border: 1px solid rgba(184,144,42,0.2);
}
.isc-meter {
  margin-bottom: 20px;
}

/* ── Stop row ── */
.isc-stop-row {
  display: flex;
  justify-content: center;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e8e4da;
}

.isc-btn-stop {
  background: transparent;
  border: none;
  color: #999;
  font-size: 12px;
  font-weight: 500;
  font-family: 'Jost', sans-serif;
  cursor: pointer;
  transition: color 0.2s;
  padding: 4px 8px;
  letter-spacing: 0.03em;
}
.isc-btn-stop:hover { color: #b91c1c; }

/* ── Complete ── */
.isc-complete { text-align: center; }
.isc-stopped-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(184,144,42,0.08);
  border: 1px solid rgba(184,144,42,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  margin: 0 auto 18px;
}
.isc-complete-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(184,144,42,0.1);
  border: 1px solid rgba(184,144,42,0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  margin: 0 auto 18px;
}
</style>

<!-- Non-scoped: override child component styles within .isc-card only -->
<style>
/* ── CSS variable overrides — cascade into QuestionBlock, QuestionContainerBlock, ConfidenceMeter ── */
.isc-card {
  --mt-accent-blue: #B8902A;
  --mt-accent-blue-muted: rgba(184, 144, 42, 0.12);
  --mt-primary: #B8902A;
  --mt-primary-dark: #9a7520;
  --mt-primary-light: #d4a843;
  --mt-border: #e0dbd0;
  --mt-card-bg: #fffdf7;
  --mt-card-border: #e8e4da;
  --mt-track-bg: #ede9e0;
  --mt-text-muted: #888;
  --mt-hover: rgba(184, 144, 42, 0.06);
  --mt-text: #0D1628;
}

/* ── Question container card ── */
.isc-card .mt-card {
  background: #fffdf7;
  border-color: #e0dbd0;
  border-radius: 8px;
}

/* ── All buttons: Manuscript shape + font ── */
.isc-card .mt-btn {
  border-radius: 4px;
  font-family: 'Jost', sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

/* ── Primary (Submit / Next) button: gold + navy text ── */
.isc-card .mt-btn-primary {
  background: #B8902A;
  color: #0D1628;
  box-shadow: none;
}
.isc-card .mt-btn-primary:hover:not(:disabled) {
  background: #d4a843;
  box-shadow: none;
  transform: translateY(-1px);
}

/* ── Text input ── */
.isc-card .mt-input {
  background: #fffdf7;
  border-color: #e0dbd0;
  border-radius: 4px;
  font-family: 'Jost', sans-serif;
  color: #0D1628;
}
.isc-card .mt-input:focus {
  border-color: #B8902A;
  outline: none;
  box-shadow: 0 0 0 2px rgba(184, 144, 42, 0.2);
}

/* ── "Sub-questions answered" count label ── */
.isc-card .mt-4.flex.items-center.justify-between .text-sm {
  font-family: 'Jost', sans-serif;
}
</style>
